/**
 * Editor V2 - Main Layout
 * Two-column layout: Left (Sidebar + PropertyPanel), Right (Preview).
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { EnhancedPreview } from './EnhancedPreview';
import { Sidebar } from './Sidebar';
import type { SidebarTemplateInfo } from './Sidebar';
import { PropertyPanel } from './PropertyPanel';
import { useSchema, useEditorContext, useIsDirty } from '../../schema/hooks/useEditor';
import { useLanguage } from '../../i18nContext';
import { saveSchema } from '../../services/editorSchema';
import type { EditorSchemaDTO } from '../../services/editorSchema';
import { getToken } from '../../services/api';
import type { NoteBlock } from '../../types';
import './EditorV2.css';

interface EditorV2Props {
  meetingNotes?: NoteBlock[];
  originalNotes?: NoteBlock[];
  mode?: 'preview' | 'edit';
  onNotesChange?: (notes: NoteBlock[]) => void;
  taskId?: string | null;
  activeLinkId?: string | null;
  linkedIds?: Set<string>;
  onLinkToggle?: (id: string) => void;
  matchedNoteIds?: Set<string>;
  topLinkedId?: string | null;
  taskTitle?: string;
  /** 模板中心"应用模板"回调，通知 Workspace 右侧预览重新加载 schema */
  onApplyTemplate?: (type?: string, schemaId?: string) => void;
  /** 任务绑定的模板类型 */
  appliedSchemaType?: string;
  /** 任务绑定的模板ID */
  appliedSchemaId?: string;
  /** 返回编辑纪要回调 */
  onBack?: () => void;
  /** 是否处于模板中心模式 */
  isTemplateCenter?: boolean;
  /** 切换模板中心/编辑纪要 */
  onToggleTemplateCenter?: () => void;
  /** 后端保存的编辑版纪要 */
  savedEditedNotes?: NoteBlock[] | null;
}

export function EditorV2({ meetingNotes, originalNotes, mode = 'preview', onNotesChange, taskId, activeLinkId, linkedIds, onLinkToggle, matchedNoteIds, topLinkedId, taskTitle, onApplyTemplate, appliedSchemaType, appliedSchemaId, onBack, isTemplateCenter, onToggleTemplateCenter, savedEditedNotes }: EditorV2Props) {
  const schema = useSchema();
  const { state } = useEditorContext();
  const isDirty = useIsDirty();
  const { t } = useLanguage();
  const tc = t.templateCenter;
  const [applySuccess, setApplySuccess] = useState(false);
  const [previewMode, setPreviewMode] = useState<'edited' | 'original'>(() => {
    // 进入模板中心时，沿用编辑纪要页面的当前视图
    try {
      const saved = localStorage.getItem('preview-view-mode');
      if (saved === 'original' || saved === 'edited') return saved;
    } catch {}
    return (savedEditedNotes && savedEditedNotes.length > 0) ? 'edited' : 'original';
  });
  const [activeUserSchemaId, setActiveUserSchemaId] = useState('');
  const [templateInfo, setTemplateInfo] = useState<SidebarTemplateInfo>({ type: 'none', presetId: '', userSchemaId: '', userSchemaName: '' });
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [autoSaveTime, setAutoSaveTime] = useState<string | null>(null);
  const [userSchemas, setUserSchemas] = useState<EditorSchemaDTO[]>([]);
  const templateChangeRef = useRef<((value: string) => void) | null>(null);
  const sidebarContextMenuRef = useRef<((e: React.MouseEvent) => void) | null>(null);

  // 是否显示保存按钮：有修改 且 当前选中了某个模板
  const showSaveBtn = isDirty && templateInfo.type !== 'none';

  // Sidebar 切换模板时自动应用
  const handleSidebarApply = useCallback((schemaId: string) => {
    onApplyTemplate?.('user', schemaId);
    setApplySuccess(true);
    setTimeout(() => setApplySuccess(false), 2000);
  }, [onApplyTemplate]);

  // 保存按钮点击 — 统一直接覆盖保存
  const handleSave = useCallback(() => {
    if (templateInfo.type !== 'user') return;
    setSaving(true);
    saveSchema({
      schemaId: templateInfo.userSchemaId,
      name: templateInfo.userSchemaName,
      numberingSchemas: state.schema.numberingSchemas,
      paragraphStyles: state.schema.paragraphStyles,
      editableMaxLevels: state.editableMaxLevels,
      selectedAbstractNumId: state.selectedAbstractNumId,
    })
      .then(() => {
        setSaving(false);
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 2000);
      })
      .catch(err => {
        console.error('Save failed:', err);
        setSaving(false);
      });
  }, [templateInfo, state.schema, state.editableMaxLevels, state.selectedAbstractNumId]);

  // 自动保存：每分钟自动保存
  useEffect(() => {
    if (templateInfo.type !== 'user' || !isDirty) return;
    const timer = setInterval(() => {
      saveSchema({
        schemaId: templateInfo.userSchemaId,
        name: templateInfo.userSchemaName,
        numberingSchemas: state.schema.numberingSchemas,
        paragraphStyles: state.schema.paragraphStyles,
        editableMaxLevels: state.editableMaxLevels,
        selectedAbstractNumId: state.selectedAbstractNumId,
      }).then(() => {
        const now = new Date();
        setAutoSaveTime(`${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}:${now.getSeconds().toString().padStart(2, '0')}`);
      }).catch(err => console.error('Auto-save failed:', err));
    }, 60000);
    return () => clearInterval(timer);
  }, [templateInfo, isDirty, state.schema, state.editableMaxLevels, state.selectedAbstractNumId]);

  // 预览模式：只显示纪要预览，不显示编辑组件
  if (mode === 'preview') {
    return (
      <EnhancedPreview meetingNotes={meetingNotes} originalNotes={originalNotes} onNotesChange={onNotesChange} taskId={taskId} activeLinkId={activeLinkId} linkedIds={linkedIds} onLinkToggle={onLinkToggle} matchedNoteIds={matchedNoteIds} topLinkedId={topLinkedId} taskTitle={taskTitle} isTemplateCenter={isTemplateCenter} onToggleTemplateCenter={onToggleTemplateCenter} onApplyTemplate={onApplyTemplate} appliedSchemaId={appliedSchemaId} savedEditedNotes={savedEditedNotes} />
    );
  }

  // 编辑模式：左右两栏编辑器
  return (
    <div className="editor-app schema-editor-root">
      {/* Header */}
      <header className="app-header">
        <div className="header-brand" style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1 }}>
          <span className="brand-name" style={{ fontSize: '13px', fontWeight: 600, whiteSpace: 'nowrap' }}>{tc.schema}</span>
          <select
            className="template-select"
            value={templateInfo.userSchemaId}
            onChange={e => templateChangeRef.current?.(e.target.value)}
            style={{ fontSize: '13px', fontWeight: 500, maxWidth: 200 }}
          >
            <option value="" hidden></option>
            {userSchemas.map(s => (
              <option key={s.schemaId} value={s.schemaId}>{s.name}</option>
            ))}
          </select>
          {getToken() && (
            <button
              className="icon-btn user-schema-menu-btn"
              onClick={e => sidebarContextMenuRef.current?.(e)}
              style={{ fontSize: '16px', padding: '2px 6px', border: 'none', background: 'transparent', cursor: 'pointer' }}
            >⋮</button>
          )}
          {autoSaveTime && (
            <span style={{ fontSize: '11px', color: '#9ca3af', marginLeft: 6 }}>
              {autoSaveTime} {tc.saved}
            </span>
          )}
          {showSaveBtn && (
            <button
              onClick={handleSave}
              disabled={saving}
              style={{
                padding: '4px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '12px',
                fontWeight: 500,
                cursor: saving ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s',
                background: saveSuccess ? '#10b981' : '#fff',
                color: saveSuccess ? '#fff' : '#374151',
                marginLeft: 4,
                opacity: saving ? 0.6 : 1,
              }}
            >
              {saveSuccess ? `✓ ${tc.saved}` : saving ? tc.saving : tc.save}
            </button>
          )}
          {onBack && (
            <>
              <div style={{ flex: 1 }} />
              <button
                onClick={onBack}
                style={{
                  padding: '6px 12px',
                  border: '1px solid #1a2e05',
                  borderRadius: '6px',
                  fontSize: '12px',
                  fontWeight: 500,
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  background: '#1a2e05',
                  color: '#fff',
                  whiteSpace: 'nowrap',
                }}
              >
                {t.workspace.editNotes}
              </button>
            </>
          )}
        </div>
      </header>

      {/* Main Body - 左侧双列设置 + 右侧预览 */}
      <div className="app-body">
        {/* 左半：Sidebar + PropertyPanel 左右并排 */}
        <div className="app-left-panel">
          <Sidebar
            onActiveUserSchemaChange={setActiveUserSchemaId}
            onTemplateInfoChange={setTemplateInfo}
            onApplyTemplate={handleSidebarApply}
            onUserSchemasChange={setUserSchemas}
            onTemplateChangeRef={templateChangeRef}
            onContextMenuRef={sidebarContextMenuRef}
            appliedSchemaType={appliedSchemaType}
            appliedSchemaId={appliedSchemaId}
            hideTemplateSelect
          />
          <div className="app-inspector-inline">
            <PropertyPanel />
          </div>
        </div>

        {/* 右半：预览区 */}
        <main className="app-main">
          <EnhancedPreview meetingNotes={meetingNotes} originalNotes={originalNotes} isSchemaEditMode taskId={taskId} taskTitle={taskTitle} showDemoText={false} showOriginal={previewMode === 'original'} savedEditedNotes={savedEditedNotes} />
        </main>
      </div>
    </div>
  );
}

export default EditorV2;
